/**
 * #10766 (H4) — the pure half of the header search box (HeaderSearch.tsx; a distinct name, as macOS is case-insensitive): the request URL, the
 * answer parser and the keyboard step. The worker route is src/routes/site-search-api.ts.
 */

export const SEARCH_ENDPOINT = '/api/search';
export const MIN_QUERY = 2;
export const DEBOUNCE_MS = 250;
/** The page an empty result points to (a free consult). */
export const CONSULT_HREF = '/free-consults';

export const COPY = {
  open: 'Search the site',
  label: 'Search SmallBiz Innovation',
  placeholder: 'Search guides, programs, tools...',
  close: 'Close search',
  hint: 'Type at least 2 letters.',
  searching: 'Searching...',
  empty: 'Nothing matched that yet. Bring the question to a free consult and we will work it through with you.',
  consult: 'Book a free consult',
  failed: 'Search is not answering right now. Try again in a moment, or book a free consult.',
} as const;

export interface HeaderSearchResult {
  title: string;
  url: string;
  description: string | null;
}

/** The search URL for a query, or null when the query is too short to send. Pure. */
export function searchUrl(query: string): string | null {
  const q = query.replace(/\s+/g, ' ').trim();
  return q.length >= MIN_QUERY ? `${SEARCH_ENDPOINT}?q=${encodeURIComponent(q.slice(0, 200))}` : null;
}

/** The results of a /api/search answer; anything malformed is dropped. Pure. */
export function parseResults(body: unknown): HeaderSearchResult[] {
  const results = (body as { results?: unknown })?.results;
  if (!Array.isArray(results)) return [];
  return results.flatMap((r) => {
    const o = (r ?? {}) as Record<string, unknown>;
    if (typeof o.title !== 'string' || typeof o.url !== 'string' || !o.url.startsWith('https://')) return [];
    return [{ title: o.title, url: o.url, description: typeof o.description === 'string' ? o.description : null }];
  });
}

/** The next active row for an arrow key: -1 is the input, and it wraps at both ends. Pure. */
export function stepActive(active: number, key: 'ArrowDown' | 'ArrowUp', count: number): number {
  if (count === 0) return -1;
  if (key === 'ArrowDown') return active >= count - 1 ? 0 : active + 1;
  return active <= 0 ? count - 1 : active - 1;
}
